import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { RefreshCw, LayoutGrid, FileText, Inbox, CheckCircle } from 'lucide-react'
import { ContentTable } from '../components/ContentTable'
import { MockupGenerateDialog } from '../components/MockupGenerateDialog'
import { BlogGenerateDialog } from '../components/BlogGenerateDialog'
import { useContentStats, useContentItems, useCreateAcademyDraft, CONTENT_TYPE_LABELS, CONTENT_STATUS_LABELS } from '../hooks/useContent'
import type { ContentItem, ContentType, ContentStatus } from '../api/contentApi'

const TYPE_TABS: (ContentType | 'all')[] = ['all', 'blog_editorial', 'room_collection', 'artwork_editorial', 'artist_editorial', 'recommendations', 'mockup', 'gallery_set', 'hero_asset']
const STATUSES: ContentStatus[] = ['draft', 'review', 'active', 'seasonal', 'archived', 'rejected']

export function ContentQueuePage() {
  const navigate = useNavigate()
  const createAcademy = useCreateAcademyDraft()
  const [type, setType] = useState<ContentType | 'all'>('all')
  const [status, setStatus] = useState<ContentStatus | 'all'>('all')

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useContentStats()
  const { data, isLoading, refetch } = useContentItems({
    type: type === 'all' ? undefined : type,
    status: status === 'all' ? undefined : status,
    limit: 100,
  })

  const agg = (st: ContentStatus) => {
    if (!stats) return 0
    const types = type === 'all' ? Object.keys(stats.byType) : [type]
    return types.reduce((n, t) => n + ((stats.byType[t] && stats.byType[t][st]) || 0), 0)
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Content Curation</h1>
          <p className="text-muted-foreground">
            Review and publish AI-drafted editorial — rooms, artwork, artists, and hero assets.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={createAcademy.isPending}
            onClick={() => createAcademy.mutate(undefined, { onSuccess: item => navigate(`/app/content/${item.id}`) })}
          >
            <FileText className="h-4 w-4 mr-2" />
            {createAcademy.isPending ? 'Creating…' : 'New Academy story'}
          </Button>
          <BlogGenerateDialog />
          <MockupGenerateDialog />
          <Button
            variant="outline"
            onClick={() => {
              refetch()
              refetchStats()
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="In Review" value={agg('review')} icon={<Inbox className="h-4 w-4 text-blue-500" />} loading={statsLoading} highlight />
        <Stat label="Drafts" value={agg('draft')} icon={<FileText className="h-4 w-4" />} loading={statsLoading} />
        <Stat label="Active" value={agg('active') + agg('seasonal')} icon={<CheckCircle className="h-4 w-4 text-green-500" />} loading={statsLoading} />
        <Stat label="Total" value={stats?.total} icon={<LayoutGrid className="h-4 w-4" />} loading={statsLoading} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Queue</CardTitle>
            <CardDescription>Agent drafts plus the live library — click an item to review and publish.</CardDescription>
          </div>
          <Select value={status} onValueChange={(v) => setStatus(v as ContentStatus | 'all')}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {CONTENT_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <Tabs value={type} onValueChange={(v) => setType(v as ContentType | 'all')}>
            <TabsList>
              {TYPE_TABS.map((t) => (
                <TabsTrigger key={t} value={t}>
                  {t === 'all' ? 'All' : CONTENT_TYPE_LABELS[t]}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value={type} className="mt-4">
              <ContentTable
                items={data?.data || []}
                onView={(i: ContentItem) => navigate(`/app/content/${i.id}`)}
                isLoading={isLoading}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

function Stat({
  label,
  value,
  icon,
  loading,
  highlight,
}: {
  label: string
  value?: number
  icon: React.ReactNode
  loading?: boolean
  highlight?: boolean
}) {
  return (
    <Card className={highlight && (value ?? 0) > 0 ? 'border-blue-200 dark:border-blue-900' : ''}>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-2">
          {icon}
          {label}
        </CardDescription>
      </CardHeader>
      <CardContent>{loading ? <Skeleton className="h-7 w-12" /> : <p className="text-2xl font-bold">{value ?? 0}</p>}</CardContent>
    </Card>
  )
}
